/**
 * 预置的 5 档固定水印（URL-Safe Base64 编码）
 * ss: res/watermark/ql_watermark_ss.png (24*8)
 * s:  res/watermark/ql_watermark_s.png (56*19)
 * m:  res/watermark/ql_watermark_m.png (128*43)
 * l:  res/watermark/ql_watermark_l.png (256*86)
 * ll: res/watermark/ql_watermark_ll.png (384*128)
 */
const WATERMARK_TIERS = [
  { width: 24, base64: 'cmVzL3dhdGVybWFyay9xbF93YXRlcm1hcmtfc3MucG5n' },
  { width: 56, base64: 'cmVzL3dhdGVybWFyay9xbF93YXRlcm1hcmtfcy5wbmc' },
  { width: 128, base64: 'cmVzL3dhdGVybWFyay9xbF93YXRlcm1hcmtfbS5wbmc' },
  { width: 256, base64: 'cmVzL3dhdGVybWFyay9xbF93YXRlcm1hcmtfbC5wbmc' },
  { width: 384, base64: 'cmVzL3dhdGVybWFyay9xbF93YXRlcm1hcmtfbGwucG5n' },
]
/**
 * OSS 水印位置: g_se 代表右下角 (SouthEast)
 * 如果需要调整间距，可以改为类似 'g_se,x_10,y_10'
 */
export const OSS_WATERMARK_POSITION = 'g_se'

/**
 * 预置的 4 档 AI 生成标识固定水印（URL-Safe Base64 编码）
 * ss: res/watermark/ai_watermark-ss.png (24*8)
 * s:  res/watermark/ai_watermark-s.png (56*19)
 * m:  res/watermark/ai_watermark-m.png (128*43)
 * l:  res/watermark/ai_watermark-l.png (256*86)
 */
const AI_WATERMARK_TIERS = [
  { width: 24, base64: 'cmVzL3dhdGVybWFyay9haV93YXRlcm1hcmstc3MucG5n' },
  { width: 56, base64: 'cmVzL3dhdGVybWFyay9haV93YXRlcm1hcmstcy5wbmc' },
  { width: 128, base64: 'cmVzL3dhdGVybWFyay9haV93YXRlcm1hcmstbS5wbmc' },
  { width: 256, base64: 'cmVzL3dhdGVybWFyay9haV93YXRlcm1hcmstbC5wbmc' },
]

/**
 * 构造 OSS 的水印处理参数串
 * @param targetImageWidth 目标主图的真实宽度，用于匹配最合适的固定档位水印
 * @param enableAiWatermark 是否添加左上角 AI 水印标识
 * @param isUserVip 用户是否是 VIP
 */
export function getOssWatermarkProcessString(targetImageWidth?: number, enableAiWatermark = true, isUserVip = false) {
  // 默认使用 m 档 (128px) 兜底
  let watermarkSource = WATERMARK_TIERS[2].base64
  let aiWatermarkSource = AI_WATERMARK_TIERS[2].base64

  if (targetImageWidth && targetImageWidth > 0) {
    // 理想宽度是主图宽度的 5%
    const idealWidth = targetImageWidth * 0.05

    // --- 寻找最合适的主水印 ---
    let closestTier = WATERMARK_TIERS[0]
    let minDiff = Math.abs(idealWidth - closestTier.width)

    for (let i = 1; i < WATERMARK_TIERS.length; i++) {
      const diff = Math.abs(idealWidth - WATERMARK_TIERS[i].width)
      if (diff < minDiff) {
        minDiff = diff
        closestTier = WATERMARK_TIERS[i]
      }
    }
    watermarkSource = closestTier.base64

    const idealAiWidth = targetImageWidth * 0.1

    // --- 寻找最合适的 AI 水印 ---
    let closestAiTier = AI_WATERMARK_TIERS[0]
    let minAiDiff = Math.abs(idealAiWidth - closestAiTier.width)

    for (let i = 1; i < AI_WATERMARK_TIERS.length; i++) {
      const diff = Math.abs(idealAiWidth - AI_WATERMARK_TIERS[i].width)
      if (diff < minAiDiff) {
        minAiDiff = diff
        closestAiTier = AI_WATERMARK_TIERS[i]
      }
    }
    aiWatermarkSource = closestAiTier.base64
  }

  // 动态尺寸的主水印在右下角 (g_se)，增加 10px 的安全边距
  // VIP 用户不需要主水印
  const mainWatermarkStr = isUserVip ? '' : `watermark,image_${watermarkSource},${OSS_WATERMARK_POSITION},x_10,y_10`

  // 如果参数要求不加 AI 水印，则仅返回主水印
  if (!enableAiWatermark) {
    return mainWatermarkStr
  }

  // AI 水印固定在左上角 (g_nw)，增加 10px 的安全边距
  const aiWatermarkStr = `watermark,image_${aiWatermarkSource},g_nw,x_10,y_10`

  if (!mainWatermarkStr) {
    return aiWatermarkStr
  }

  // 通过 / 拼接多个 OSS 图片处理操作
  return `${aiWatermarkStr}/${mainWatermarkStr}`
}

/**
 * 给目标 OSS 图片 URL 添加水印参数
 * @param url 原始图片 URL
 * @param targetImageWidth 目标主图的真实宽度（可选）
 * @param enableAiWatermark 是否添加左上角 AI 水印标识
 * @param isUserVip 用户是否是 VIP
 * @returns 附加了水印参数的图片 URL
 */
export function appendWatermarkToOssUrl(url: string, targetImageWidth?: number, enableAiWatermark = true, isUserVip = false): string {
  if (!url) return url

  try {
    const parsed = new URL(url)
    const searchParams = new URLSearchParams(parsed.search)
    let ossProcess = searchParams.get('x-oss-process')

    const watermarkProcess = getOssWatermarkProcessString(targetImageWidth, enableAiWatermark, isUserVip)

    // 如果没有任何水印需要添加（例如 VIP 且未开启 AI 水印），则直接返回原 URL
    if (!watermarkProcess) {
      return url
    }

    if (!ossProcess) {
      ossProcess = `image/${watermarkProcess}`
    } else {
      // 如果已经有 x-oss-process 且包含了水印，直接返回，防重复添加
      if (ossProcess.includes('watermark,image_')) {
        return url
      }

      // 将 watermark 添加到处理链路中
      // 为了保证最后输出格式不受影响，如果结尾有 /format, 最好将其插在 format 操作之前
      if (ossProcess.includes('/format,')) {
        ossProcess = ossProcess.replace('/format,', `/${watermarkProcess}/format,`)
      } else {
        ossProcess = `${ossProcess}/${watermarkProcess}`
      }
    }

    searchParams.set('x-oss-process', ossProcess)

    // 重新组合 URL
    // URLSearchParams 会自动对特殊字符进行 encode，导致在处理如 x-oss-process 中的逗号和斜杠时可能会转义。
    // 很多时候 OSS 能直接识别转义后的参数，但为了保证完全兼容原生的展示，我们可以直接手动拼接 search
    parsed.search = `?x-oss-process=${ossProcess}`

    // 如果原始 url 还有其他参数，可以补充回来
    searchParams.delete('x-oss-process')
    const remainingParams = searchParams.toString()
    if (remainingParams) {
      parsed.search += `&${remainingParams}`
    }

    return parsed.toString()
  } catch (err) {
    // 遇到非法的 URL 格式时，直接返回原地址
    console.error('Failed to append watermark to URL:', err)
    return url
  }
}
